import _ from "lodash";
import { isOrderFinalUpdateSent } from "./orderFinalUpdate";

export const ORDER_LIST_COLUMNS = [
  "ORDER",
  "TOTAL ITEMS",
  "STATUS",
  "TRACKING",
  "CARRIER",
  "TRACKING URL",
];

export const ORDER_STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All Status" },
  { value: "pending", label: "Created" },
  { value: "approved", label: "Approved" },
  { value: "bill_generated", label: "Bill Generated" },
  { value: "paid", label: "Paid" },
  { value: "packing", label: "Packed" },
  { value: "shipment", label: "Shipped" },
  { value: "delivered", label: "Delivered" },
  { value: "rejected", label: "Rejected" },
];

export const orderListStatusColors = {
  pending: { background: "#9C9C9C" },
  approved: { background: "#126ABC" },
  bill_generated: { background: "#7B61FF" },
  paid: { background: "#FF8500" },
  packing: { background: "#126ABC" },
  shipment: { background: "#126ABC" },
  wait_to_deliver: { background: "#126ABC" },
  delivered: { background: "#00C046" },
  rejected: { background: "#FF3850" },
  cancelled: { background: "#9C9C9C" },
  awaiting_payment: { background: "#FF8500" },
  await_payment_approval: { background: "#FF8500" },
  pending_confirmation: { background: "#7B61FF" },
  pending_confirmation_from_shopper: { background: "#7B61FF" },
};

/** Line items in these statuses are excluded from order item totals. */
export const EXCLUDED_FROM_ITEM_COUNT_STATUSES = new Set([
  "rejected",
  "cancelled",
]);

export function getOrderDetailItemStatus(detail) {
  const item = detail?.item || {};
  return String(item?.status ?? detail?.status ?? "")
    .trim()
    .toLowerCase();
}

export function isOrderDetailCountedInTotal(detail) {
  const status = getOrderDetailItemStatus(detail);
  if (!status) return true;
  return !EXCLUDED_FROM_ITEM_COUNT_STATUSES.has(status);
}

export function resolveTrackingNumber(order) {
  const value =
    order?.tracking_number ??
    order?.tracking_no ??
    order?.tracking_id ??
    order?.shipment_tracking ??
    order?.trackingNumber;
  return value != null && String(value).trim() !== "" ? String(value) : "-";
}

export function resolveCarrier(order) {
  const value =
    order?.carrier ??
    order?.shipping_carrier ??
    order?.carrier_name ??
    order?.shippingCarrier;
  return value != null && String(value).trim() !== "" ? String(value) : "-";
}

export function resolveTrackingUrl(order) {
  const value =
    order?.tracking_url ??
    order?.trackingUrl ??
    order?.shipment_tracking_url;
  return value != null && String(value).trim() !== "" ? String(value) : "-";
}

export function resolveTrackingHref(url) {
  const trimmed = String(url ?? "").trim();
  if (!trimmed || trimmed === "-") return null;
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

export function resolveRawTrackingValue(order, field) {
  const resolvers = {
    tracking_number: resolveTrackingNumber,
    carrier: resolveCarrier,
    tracking_url: resolveTrackingUrl,
  };
  const resolved = resolvers[field]?.(order);
  return resolved === "-" ? "" : resolved;
}

export function normalizeOrderStatus(orderStatus) {
  const key = String(orderStatus ?? "").trim().toLowerCase();
  if (!key || key === "created") return "pending";
  if (key === "request_info") return "pending";
  return key;
}

export function getOrderStatusBackground(orderStatus) {
  const key = normalizeOrderStatus(orderStatus);
  return orderListStatusColors[key]?.background ?? "#9C9C9C";
}

export function formatOrderStatusLabel(orderStatus) {
  const key = normalizeOrderStatus(orderStatus);
  const labels = {
    pending: "Created",
    approved: "Approved",
    bill_generated: "Bill Generated",
    paid: "Paid",
    packing: "Packed",
    shipment: "Shipped",
    delivered: "Delivered",
    rejected: "Rejected",
    cancelled: "Cancelled",
  };
  if (labels[key]) return labels[key];
  return key ? _.startCase(key) : "Created";
}

/** Customer may add locker items only while order is still pending (initial stage). */
export function canAddProductsToOrder(order) {
  if (!order) return false;
  if (isOrderFinalUpdateSent(order)) return false;
  const status = normalizeOrderStatus(order.order_status);
  return status === "pending" || status === "approved";
}
